// ============================================================
// 📁 کنار UsersPage.tsx   (فایل جدید)  —  پنجره‌های دعوت/ویرایش/دسترسی/نشست‌ها
// ============================================================
import React, { useEffect, useState } from "react";
import { UserPlus, Edit, Shield, Monitor, Trash2, Clock, Plus, Check, GitCompare, LogOut, ArrowRight } from "lucide-react";
import { uapi, UserType, PermMap, defaultPermissions, defaultOps, emptyPerm, pageLabels, permLabels, roleLabel, fmtDT, relTime, toLocalInput } from "../../services/usersApi";
import { Modal, Btn, Field, ToggleRow, PermMatrix, OpsGrid, SectionTitle, Spinner, Empty, Badge, Toggle, toast, dlg, stepCall, notify, arr } from "./UsersUi";

export type Tpl = { id: number; name: string; description: string; base_role: string; is_system: boolean; permissions: PermMap; operations: Record<string, boolean> };
const dayNames = ["یکشنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنجشنبه", "جمعه", "شنبه"];
const dayOrder = [6, 0, 1, 2, 3, 4, 5]; // شنبه تا جمعه

function mergePerms(p?: any): PermMap {
  const m = defaultPermissions();
  Object.keys(m).forEach((k) => (m[k] = emptyPerm()));
  if (p && typeof p === "object") Object.keys(p).forEach((k) => (m[k] = { ...emptyPerm(), ...p[k] }));
  return m;
}
function TemplatePicker({ templates, onPick }: { templates: Tpl[]; onPick: (t: Tpl) => void }) {
  if (!templates.length) return null;
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
      <span style={{ fontSize: 12, opacity: 0.6, alignSelf: "center" }}>قالب آماده:</span>
      {templates.map((t) => (
        <button key={t.id} type="button" className="um-fchip" title={t.description} onClick={() => onPick(t)}>{t.name}</button>
      ))}
    </div>
  );
}

/* ============================== دعوت ============================== */
export function InviteModal({ templates, onClose, onDone }: { templates: Tpl[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ fullName: "", username: "", email: "", phone: "", role: "user", jobTitle: "", department: "", approvalLimit: "" });
  const [perms, setPerms] = useState<PermMap>(defaultPermissions());
  const [ops, setOps] = useState<Record<string, boolean>>(defaultOps());
  const [busy, setBusy] = useState(false);
  const set = (k: string, v: string) => setF((o) => ({ ...o, [k]: v }));
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email);
  const valid = f.fullName.trim() && /^[A-Za-z0-9._-]{3,50}$/.test(f.username) && emailOk;
  const applyTpl = (t: Tpl) => { setPerms(mergePerms(t.permissions)); setOps({ ...defaultOps(), ...t.operations }); if (t.base_role && f.role !== "admin") set("role", t.base_role); toast(`قالب «${t.name}» اعمال شد`, "info"); };
  const submit = async () => {
    setBusy(true);
    const r = await stepCall((pw) => uapi.invite({ ...f, approvalLimit: f.approvalLimit === "" ? null : +f.approvalLimit, permissions: perms, operations: ops, confirmPassword: pw }));
    setBusy(false);
    if (r && r.success) { toast(r.message || "دعوت ارسال شد", r.emailSent ? "ok" : "err"); onDone(); onClose(); } else notify(r);
  };
  return (
    <Modal title="دعوت کاربر جدید" subtitle="یک لینک امن و یک‌بار مصرف به ایمیل کاربر ارسال می‌شود؛ کاربر خودش پسورد را تعیین می‌کند" icon={<UserPlus size={22} color="#3b82f6" />} width={820} onClose={onClose}
      footer={<><Btn kind="ghost" onClick={onClose}>انصراف</Btn><Btn kind="gold" icon={<UserPlus size={15} />} loading={busy} disabled={!valid} onClick={submit}>ارسال دعوت‌نامه</Btn></>}>
      <div className="um-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))" }}>
        <Field label="نام کامل *"><input className="um-input" value={f.fullName} onChange={(e) => set("fullName", e.target.value)} /></Field>
        <Field label="ایمیل *" hint={f.email && !emailOk ? "ایمیل معتبر نیست" : "لینک دعوت به این ایمیل می‌رود"}>
          <input className="um-input" dir="ltr" value={f.email} onChange={(e) => { set("email", e.target.value); if (!f.username) set("username", e.target.value.split("@")[0].replace(/[^A-Za-z0-9._-]/g, "").slice(0, 30)); }} />
        </Field>
        <Field label="نام کاربری *" hint="حروف انگلیسی، عدد، نقطه، خط تیره"><input className="um-input" dir="ltr" value={f.username} onChange={(e) => set("username", e.target.value)} /></Field>
        <Field label="تلفن"><input className="um-input" dir="ltr" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="نقش">
          <select className="um-select" value={f.role} onChange={(e) => set("role", e.target.value)}>
            <option value="user">کاربر عادی</option><option value="manager">مدیر</option><option value="admin">ادمین (کامل)</option>
          </select>
        </Field>
        <Field label="عنوان شغلی"><input className="um-input" value={f.jobTitle} onChange={(e) => set("jobTitle", e.target.value)} /></Field>
        <Field label="بخش / دپارتمان"><input className="um-input" value={f.department} onChange={(e) => set("department", e.target.value)} /></Field>
        <Field label="سقف مبلغ تایید (دلار)" hint="خالی = بدون سقف"><input className="um-input" dir="ltr" type="number" min={0} value={f.approvalLimit} onChange={(e) => set("approvalLimit", e.target.value)} /></Field>
      </div>
      {f.role === "admin" ? (
        <div className="um-card" style={{ marginTop: 18, borderColor: "rgba(245,158,11,.4)" }}>
          <b style={{ color: "#f59e0b" }}>ادمین به همه بخش‌ها دسترسی کامل دارد.</b>
          <div className="um-hint">دعوت ادمین نیاز به تایید پسورد شما دارد.</div>
        </div>
      ) : (
        <>
          <SectionTitle>دسترسی‌ها</SectionTitle>
          <TemplatePicker templates={templates} onPick={applyTpl} />
          <PermMatrix value={perms} onChange={setPerms} />
          <SectionTitle>مجوزهای عملیاتی</SectionTitle>
          <OpsGrid value={ops} onChange={setOps} />
        </>
      )}
    </Modal>
  );
}

/* ============================== ویرایش ============================== */
export function EditModal({ user, onClose, onDone }: { user: UserType; onClose: () => void; onDone: () => void }) {
  const wh = user.work_hours;
  const [f, setF] = useState({ fullName: user.full_name, email: user.email || "", phone: user.phone || "", role: user.role as string, jobTitle: user.job_title || "", department: user.department || "", approvalLimit: user.approval_limit === null || user.approval_limit === undefined ? "" : String(user.approval_limit), ipWhitelist: (user.ip_whitelist || "").replace(/[,;]+/g, "\n") });
  const [hoursOn, setHoursOn] = useState(!!(wh && wh.from));
  const [hFrom, setHFrom] = useState(wh?.from || "08:00");
  const [hTo, setHTo] = useState(wh?.to || "18:00");
  const [days, setDays] = useState<number[]>(wh?.days || [6, 0, 1, 2, 3, 4]);
  const [scopeOn, setScopeOn] = useState(!!(user.data_scope && user.data_scope.warehouses && user.data_scope.warehouses.length));
  const [scope, setScope] = useState<number[]>(user.data_scope?.warehouses || []);
  const [whs, setWhs] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { uapi.warehouses().then((r) => setWhs(arr(r, "warehouses", "data", "items", "list"))).catch(() => {}); }, []);
  const set = (k: string, v: string) => setF((o) => ({ ...o, [k]: v }));
  const submit = async () => {
    setBusy(true);
    const body: any = { id: user.id, ...f, ipWhitelist: f.ipWhitelist.split(/[\s,;]+/).filter(Boolean).join(", "), approvalLimit: f.approvalLimit === "" ? null : +f.approvalLimit,
      workHours: hoursOn ? { from: hFrom, to: hTo, days } : null, dataScope: scopeOn && scope.length ? { warehouses: scope } : null };
    const r = await stepCall((pw) => uapi.update({ ...body, confirmPassword: pw }));
    setBusy(false);
    if (notify(r, "ذخیره شد")) { onDone(); onClose(); }
  };
  return (
    <Modal title="ویرایش کاربر" subtitle={`@${user.username}`} icon={<Edit size={22} color="#3b82f6" />} width={760} onClose={onClose}
      footer={<><Btn kind="ghost" onClick={onClose}>انصراف</Btn><Btn loading={busy} icon={<Check size={15} />} onClick={submit}>ذخیره تغییرات</Btn></>}>
      <div className="um-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))" }}>
        <Field label="نام کامل"><input className="um-input" value={f.fullName} onChange={(e) => set("fullName", e.target.value)} /></Field>
        <Field label="ایمیل"><input className="um-input" dir="ltr" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="تلفن"><input className="um-input" dir="ltr" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="نقش" hint="تغییر نقش از/به ادمین نیاز به تایید پسورد دارد">
          <select className="um-select" value={f.role} onChange={(e) => set("role", e.target.value)}>
            <option value="user">کاربر عادی</option><option value="manager">مدیر</option><option value="admin">ادمین</option>
          </select>
        </Field>
        <Field label="عنوان شغلی"><input className="um-input" value={f.jobTitle} onChange={(e) => set("jobTitle", e.target.value)} /></Field>
        <Field label="دپارتمان"><input className="um-input" value={f.department} onChange={(e) => set("department", e.target.value)} /></Field>
        <Field label="سقف مبلغ تایید (دلار)" hint="خالی = بدون سقف"><input className="um-input" dir="ltr" type="number" min={0} value={f.approvalLimit} onChange={(e) => set("approvalLimit", e.target.value)} /></Field>
      </div>

      <SectionTitle>محدودیت‌های ورود</SectionTitle>
      <Field label="IP های مجاز (هر خط یک مورد)" hint="مثال: 185.10.20.30  یا  185.10.20.0/24  یا  185.10.*.* — خالی = بدون محدودیت. با اشتباه، کاربر بیرون می‌ماند؛ مراقب باشید.">
        <textarea className="um-textarea" dir="ltr" rows={3} value={f.ipWhitelist} onChange={(e) => set("ipWhitelist", e.target.value)} />
      </Field>
      <ToggleRow label="محدودیت ساعت کاری" hint="خارج از این ساعت‌ها (به وقت کابل) ورود ممکن نیست" on={hoursOn} onChange={setHoursOn} />
      {hoursOn && (
        <div style={{ padding: "12px 0" }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <Field label="از ساعت"><input className="um-input" type="time" value={hFrom} onChange={(e) => setHFrom(e.target.value)} /></Field>
            <Field label="تا ساعت"><input className="um-input" type="time" value={hTo} onChange={(e) => setHTo(e.target.value)} /></Field>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {dayOrder.map((d) => (
              <button key={d} type="button" className={`um-fchip ${days.includes(d) ? "on" : ""}`} onClick={() => setDays(days.includes(d) ? days.filter((x) => x !== d) : [...days, d])}>{dayNames[d]}</button>
            ))}
          </div>
        </div>
      )}
      <ToggleRow label="محدود کردن به گودام‌های مشخص" hint="دامنه داده‌ی کاربر" on={scopeOn} onChange={setScopeOn} />
      {scopeOn && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "12px 0" }}>
          {whs.length === 0 && <span className="um-hint">لیست گودام‌ها در دسترس نیست</span>}
          {whs.map((w: any) => (
            <button key={w.id} type="button" className={`um-fchip ${scope.includes(+w.id) ? "on" : ""}`} onClick={() => setScope(scope.includes(+w.id) ? scope.filter((x) => x !== +w.id) : [...scope, +w.id])}>{w.name || w.title || `گودام ${w.id}`}</button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ============================== دسترسی‌ها ============================== */
export function PermModal({ user, users, templates, onClose, onDone }: { user: UserType; users: UserType[]; templates: Tpl[]; onClose: () => void; onDone: () => void }) {
  const isAdminUser = user.role === "admin";
  const [perms, setPerms] = useState<PermMap>(mergePerms(user.permissions));
  const [ops, setOps] = useState<Record<string, boolean>>({ ...defaultOps(), ...(user.operations || {}) });
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<"perm" | "grants" | "effective" | "compare">("perm");
  const [eff, setEff] = useState<any>(null);
  const [cmpId, setCmpId] = useState(0);
  const [cmp, setCmp] = useState<any>(null);
  const [g, setG] = useState({ page: "finance", view: true, add: false, edit: false, delete: false, approve: false, startsAt: toLocalInput(new Date()), endsAt: "", reason: "" });

  const loadEff = async () => { const r = await uapi.effective(user.id); if (r.success) setEff(r); };
  useEffect(() => { if (view === "effective" || view === "grants") loadEff(); }, [view]); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    const r = await uapi.updatePermissions(user.id, perms, ops);
    setBusy(false);
    if (notify(r)) { onDone(); onClose(); }
  };
  const copyFrom = async (fromId: number) => {
    if (!fromId) return;
    const src = users.find((u) => u.id === fromId); if (!src) return;
    if (!(await dlg.confirm("کپی دسترسی‌ها", `دسترسی‌های «${src.full_name}» جایگزین دسترسی‌های «${user.full_name}» می‌شود. ادامه می‌دهید؟`))) return;
    const r = await uapi.copyPermissions(fromId, user.id);
    if (notify(r)) { onDone(); onClose(); }
  };
  const addGrant = async () => {
    if (!g.endsAt) { toast("زمان پایان را مشخص کنید", "err"); return; }
    const r = await uapi.grantAdd({ userId: user.id, page: g.page, perms: { view: g.view, add: g.add, edit: g.edit, delete: g.delete, approve: g.approve }, startsAt: g.startsAt.replace("T", " "), endsAt: g.endsAt.replace("T", " "), reason: g.reason });
    if (notify(r)) { setG({ ...g, reason: "" }); loadEff(); onDone(); }
  };
  const revokeGrant = async (id: number) => { const r = await uapi.grantRevoke(id); if (notify(r)) { loadEff(); onDone(); } };
  const doCompare = async (id: number) => { setCmpId(id); if (!id) { setCmp(null); return; } const r = await uapi.compare(user.id, id); if (r.success) setCmp(r); };
  const others = users.filter((u) => u.id !== user.id && u.state !== "deleted");

  const tabBtn = (k: typeof view, label: string, icon: React.ReactNode) => (
    <button type="button" className={`um-fchip ${view === k ? "on" : ""}`} style={{ display: "inline-flex", gap: 6, alignItems: "center" }} onClick={() => setView(k)}>{icon}{label}</button>
  );
  const summary = (e: any) => Object.keys(pageLabels).map((p) => {
    const x = e?.[p]; if (!x) return null;
    const on = (Object.keys(permLabels) as (keyof typeof permLabels)[]).filter((k) => x[k]).map((k) => permLabels[k]);
    return on.length ? <div key={p} className="um-kv"><span>{pageLabels[p]}</span><span style={{ opacity: 0.85 }}>{on.join("، ")}</span></div> : null;
  });

  return (
    <Modal title={`دسترسی‌های ${user.full_name}`} subtitle={`نقش: ${roleLabel(user.role)}`} icon={<Shield size={22} color="#8b5cf6" />} width={860} onClose={onClose}
      footer={view === "perm" && !isAdminUser ? <><Btn kind="ghost" onClick={onClose}>انصراف</Btn><Btn icon={<Check size={15} />} loading={busy} onClick={save}>ذخیره دسترسی‌ها</Btn></> : <Btn kind="ghost" onClick={onClose}>بستن</Btn>}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {tabBtn("perm", "دسترسی‌های اصلی", <Shield size={13} />)}
        {tabBtn("grants", "دسترسی موقت", <Clock size={13} />)}
        {tabBtn("effective", "دسترسی نهایی", <Check size={13} />)}
        {tabBtn("compare", "مقایسه با کاربر دیگر", <GitCompare size={13} />)}
      </div>

      {view === "perm" && (isAdminUser ? <Empty icon={<Shield size={40} />} title="ادمین به همه بخش‌ها دسترسی کامل دارد" sub="برای محدود کردن، ابتدا نقش او را تغییر دهید" /> : (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
            <TemplatePicker templates={templates} onPick={(t) => { setPerms(mergePerms(t.permissions)); setOps({ ...defaultOps(), ...t.operations }); toast(`قالب «${t.name}» اعمال شد (هنوز ذخیره نشده)`, "info"); }} />
            <select className="um-select" style={{ width: 210 }} value="" onChange={(e) => copyFrom(+e.target.value)}>
              <option value="">کپی از کاربر دیگر...</option>
              {others.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <PermMatrix value={perms} onChange={setPerms} />
          <SectionTitle>مجوزهای عملیاتی</SectionTitle>
          <OpsGrid value={ops} onChange={setOps} />
        </>
      ))}

      {view === "grants" && (
        <>
          <div className="um-card">
            <div className="um-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
              <Field label="صفحه"><select className="um-select" value={g.page} onChange={(e) => setG({ ...g, page: e.target.value })}>{Object.keys(pageLabels).map((p) => <option key={p} value={p}>{pageLabels[p]}</option>)}</select></Field>
              <Field label="از"><input className="um-input" type="datetime-local" value={g.startsAt} onChange={(e) => setG({ ...g, startsAt: e.target.value })} /></Field>
              <Field label="تا"><input className="um-input" type="datetime-local" value={g.endsAt} onChange={(e) => setG({ ...g, endsAt: e.target.value })} /></Field>
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", margin: "14px 0" }}>
              {(["view", "add", "edit", "delete", "approve"] as const).map((k) => (
                <label key={k} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5 }}><Toggle on={(g as any)[k]} onChange={(v) => setG({ ...g, [k]: v })} />{permLabels[k]}</label>
              ))}
            </div>
            <Field label="دلیل *"><input className="um-input" value={g.reason} onChange={(e) => setG({ ...g, reason: e.target.value })} placeholder="مثلاً: جایگزین همکار در مرخصی" /></Field>
            <div style={{ marginTop: 14 }}><Btn kind="gold" size="sm" icon={<Plus size={14} />} onClick={addGrant}>ثبت دسترسی موقت</Btn></div>
          </div>
          <SectionTitle>سوابق دسترسی موقت</SectionTitle>
          {!eff ? <Spinner /> : arr(eff, "grants").length === 0 ? <Empty icon={<Clock size={36} />} title="موردی ثبت نشده" /> : arr(eff, "grants").map((x: any) => (
            <div key={x.id} className="um-card" style={{ padding: 14, marginBottom: 10, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <b>{pageLabels[x.page] || x.page}</b> <span className="um-hint">— {x.reason}</span>
                <div className="um-hint">{fmtDT(x.starts_at)} تا {fmtDT(x.ends_at)}</div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {+x.active ? <Badge color="#10b981" dot>فعال</Badge> : x.revoked_at ? <Badge color="#64748b">لغو شده</Badge> : <Badge color="#f59e0b">غیرفعال / تمام‌شده</Badge>}
                {+x.active === 1 && <Btn kind="danger" size="sm" onClick={() => revokeGrant(x.id)}>لغو</Btn>}
              </div>
            </div>
          ))}
        </>
      )}

      {view === "effective" && (!eff ? <Spinner /> : (
        <>
          <div className="um-hint" style={{ marginBottom: 10 }}>مجموع دسترسی اصلی + دسترسی‌های موقت فعال. سقف تایید: {eff.approval_limit === null ? "بدون سقف" : `${eff.approval_limit} دلار`}</div>
          <div className="um-card">{summary(eff.effective)}{!Object.keys(pageLabels).some((p) => eff.effective?.[p] && Object.values(eff.effective[p]).some(Boolean)) && <Empty icon={<Shield size={32} />} title="هیچ دسترسی‌ای ندارد" />}</div>
        </>
      ))}

      {view === "compare" && (
        <>
          <select className="um-select" value={cmpId} onChange={(e) => doCompare(+e.target.value)} style={{ marginBottom: 14 }}>
            <option value={0}>انتخاب کاربر برای مقایسه...</option>
            {others.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          {cmp && (
            <div className="um-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              {[cmp.a, cmp.b].map((x: any) => (
                <div key={x.id} className="um-card"><b>{x.name}</b> <span className="um-hint">({roleLabel(x.role)})</span><div style={{ marginTop: 10 }}>{summary(x.effective)}</div></div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/* ============================== نشست‌ها ============================== */
export function SessionsModal({ user, onClose, onDone }: { user: UserType; onClose: () => void; onDone: () => void }) {
  const [rows, setRows] = useState<any[] | null>(null);
  const load = async () => { const r = await uapi.sessions(user.id); setRows(arr(r, "sessions")); };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const kill = async (id: number) => { const r = await uapi.sessionRevoke(id); if (notify(r)) { load(); onDone(); } };
  const killAll = async () => { if (!(await dlg.confirm("بستن همه نشست‌ها", `کاربر «${user.full_name}» از همه دستگاه‌ها خارج می‌شود.`, true))) return; const r = await uapi.sessionsRevokeAll(user.id); if (notify(r)) { load(); onDone(); } };
  return (
    <Modal title="نشست‌های فعال" subtitle={user.full_name} icon={<Monitor size={22} color="#10b981" />} width={640} onClose={onClose}
      footer={<><Btn kind="ghost" onClick={onClose}>بستن</Btn>{!!rows?.length && <Btn kind="danger" icon={<LogOut size={15} />} onClick={killAll}>بستن همه</Btn>}</>}>
      {!rows ? <Spinner /> : rows.length === 0 ? <Empty icon={<Monitor size={36} />} title="نشست فعالی وجود ندارد" /> : rows.map((s) => (
        <div key={s.id} className="um-card" style={{ padding: 14, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div>
            <b>{s.device}</b> {s.current && <Badge color="#10b981" dot>این دستگاه</Badge>}
            <div className="um-hint"><span dir="ltr">{s.ip || "—"}</span> — آخرین فعالیت: {relTime(s.last_used_at || s.created_at)} — شروع: {fmtDT(s.created_at)}</div>
          </div>
          {!s.current && <Btn kind="danger" size="sm" onClick={() => kill(s.id)}>بستن</Btn>}
        </div>
      ))}
    </Modal>
  );
}

/* ============================== حذف / بایگانی ============================== */
export function DeleteModal({ user, users, onClose, onDone }: { user: UserType; users: UserType[]; onClose: () => void; onDone: () => void }) {
  const [to, setTo] = useState(0);
  const [busy, setBusy] = useState(false);
  const targets = users.filter((u) => u.id !== user.id && u.state === "active");
  const go = async () => {
    setBusy(true);
    const r = await stepCall((pw) => uapi.remove(user.id, pw || "", to || undefined));
    setBusy(false);
    if (notify(r)) { onDone(); onClose(); }
  };
  return (
    <Modal title="بایگانی کاربر" subtitle={user.full_name} icon={<Trash2 size={22} color="#ef4444" />} width={560} onClose={onClose}
      footer={<><Btn kind="ghost" onClick={onClose}>انصراف</Btn><Btn kind="danger" loading={busy} onClick={go}>بایگانی کاربر</Btn></>}>
      <div className="um-card" style={{ borderColor: "rgba(16,185,129,.4)", marginBottom: 16 }}>
        <b style={{ color: "#10b981" }}>هیچ سابقه مالی یا گزارشی پاک نمی‌شود.</b>
        <div className="um-hint">کاربر فقط غیرفعال و بایگانی می‌شود؛ نام او در گزارش‌ها و سوابق قبلی باقی می‌ماند و بعداً قابل بازیابی است.</div>
      </div>
      <Field label="انتقال کارهای باز او به (اختیاری)" hint="وظایف تقویم، مالکیت گروه‌های گفتگو و درخواست‌های تایید در انتظار">
        <select className="um-select" value={to} onChange={(e) => setTo(+e.target.value)}>
          <option value={0}>بدون انتقال</option>
          {targets.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
      </Field>
    </Modal>
  );
}

/* ============================== انتقال مالکیت ============================== */
export function TransferModal({ user, users, onClose, onDone }: { user: UserType; users: UserType[]; onClose: () => void; onDone: () => void }) {
  const [to, setTo] = useState(0);
  const [counts, setCounts] = useState<any>(null);
  const targets = users.filter((u) => u.id !== user.id && u.state === "active");
  const preview = async (id: number) => { setTo(id); setCounts(null); if (!id) return; const r = await uapi.transfer(user.id, id, true); if (r.success) setCounts(r.counts); else notify(r); };
  const go = async () => { const r = await uapi.transfer(user.id, to, false); if (notify(r, "مالکیت منتقل شد")) { onDone(); onClose(); } };
  const total = counts ? Object.values(counts).reduce((a: number, b: any) => a + (+b || 0), 0) : 0;
  return (
    <Modal title="انتقال مالکیت کارها" subtitle={`از ${user.full_name}`} icon={<ArrowRight size={22} color="#3b82f6" />} width={540} onClose={onClose}
      footer={<><Btn kind="ghost" onClick={onClose}>انصراف</Btn><Btn disabled={!to || !total} onClick={go}>انتقال</Btn></>}>
      <Field label="کاربر مقصد"><select className="um-select" value={to} onChange={(e) => preview(+e.target.value)}><option value={0}>انتخاب...</option>{targets.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}</select></Field>
      {counts && <div className="um-card" style={{ marginTop: 14 }}>
        {Object.keys(counts).map((k) => <div key={k} className="um-kv"><span>{({ calendar_tasks: "وظایف باز تقویم", group_chats_owned: "گروه‌های گفتگو (مالکیت)", pending_approvals_assigned: "درخواست‌های تایید در انتظار" } as any)[k] || k}</span><b>{counts[k]}</b></div>)}
        {!total && <div className="um-hint">موردی برای انتقال وجود ندارد.</div>}
      </div>}
    </Modal>
  );
}
